/**
 * Bureau Activity Monitoring API
 */

import { apiClient } from '@/core/api/client';
import {
  BureauUsersResponse,
  BureauActivityResponse,
  BureauSummaryResponse,
  BureauStatsResponse,
  RealtimeActivityResponse,
  UserDetailsResponse,
  DateRange
} from '../types/bureau-activity.types';

const BASE_URL = '/api/admin/monitoring/bureau';

/**
 * Get list of bureau users for filter dropdown
 */
export const getBureauUsers = async (): Promise<BureauUsersResponse> => {
  return apiClient.get<BureauUsersResponse>(`${BASE_URL}/users`);
};

/**
 * Get daily activity data for bureau users
 */
export const getBureauActivity = async (
  dateRange: DateRange,
  userId?: string
): Promise<BureauActivityResponse> => {
  const params = new URLSearchParams({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  });

  if (userId) {
    params.append('userId', userId);
  }

  return apiClient.get<BureauActivityResponse>(
    `${BASE_URL}/activity?${params.toString()}`
  );
};

/**
 * Get aggregated summary per user for date range
 */
export const getBureauSummary = async (
  dateRange: DateRange
): Promise<BureauSummaryResponse> => {
  const params = new URLSearchParams({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  });

  return apiClient.get<BureauSummaryResponse>(
    `${BASE_URL}/summary?${params.toString()}`
  );
};

/**
 * Get overall dashboard stats
 */
export const getBureauStats = async (
  dateRange: DateRange
): Promise<BureauStatsResponse> => {
  const params = new URLSearchParams({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  });

  return apiClient.get<BureauStatsResponse>(
    `${BASE_URL}/stats?${params.toString()}`
  );
};

/**
 * Get real-time activity for a specific user (today only)
 */
export const getRealtimeActivity = async (
  userId: string
): Promise<RealtimeActivityResponse> => {
  return apiClient.get<RealtimeActivityResponse>(
    `${BASE_URL}/realtime/${userId}`
  );
};

/**
 * Get detailed activity breakdown for a specific user
 */
export const getUserActivityDetails = async (
  userId: string,
  dateRange: DateRange
): Promise<UserDetailsResponse> => {
  const params = new URLSearchParams({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  });

  return apiClient.get<UserDetailsResponse>(
    `${BASE_URL}/user/${userId}/details?${params.toString()}`
  );
};

/**
 * Manually trigger aggregation for a specific date (admin utility)
 */
export const triggerAggregation = async (date: string): Promise<{ success: boolean }> => {
  return apiClient.post(`${BASE_URL}/aggregate`, { date });
};

/**
 * Get à faire (todo) counts per bureau user from tracker
 */
export interface TodoCountEntry {
  open: number;
  done_1d: number;
  done_7d: number;
}

export const getTodoCounts = async (): Promise<{ success: boolean; data: Record<string, TodoCountEntry> }> => {
  return apiClient.get<{ success: boolean; data: Record<string, TodoCountEntry> }>(`${BASE_URL}/todo-counts`);
};

export const bureauActivityAPI = {
  getBureauUsers,
  getBureauActivity,
  getBureauSummary,
  getBureauStats,
  getRealtimeActivity,
  getUserActivityDetails,
  triggerAggregation,
  getTodoCounts
};
